/**
 * IndexedDB Storage Utility
 *
 * Centralized localForage-based storage for all large data outside Redux state.
 * This prevents DevTools memory issues and improves performance.
 *
 * Use cases:
 * - Undo/redo history for editors
 * - Large cached data (campaigns, ads, targeting categories, etc.)
 * - Temporary form data
 * - Property management cache
 *
 * All data should be stored here instead of Redux state for performance.
 */

import localforage from "localforage";

export const STORAGE_CONFIG = {
  DB_NAME: "notifyy",
  // Cache durations
  DEFAULT_TTL_MS: 5 * 60 * 1000, // 5 minutes
  SHORT_TTL_MS: 60 * 1000, // 1 minute
  MEDIUM_TTL_MS: 30 * 60 * 1000, // 30 minutes
  LONG_TTL_MS: 24 * 60 * 60 * 1000, // 24 hours
  TARGETING_CATEGORY_TTL_MS: 7 * 24 * 60 * 60 * 1000, // 7 days (rarely changes)
  TARGET_SEARCH_TTL_MS: 30 * 60 * 1000, // 30 minutes
  PROPERTY_GROUPS_TTL_MS: 24 * 60 * 60 * 1000, // 24 hours
  // Limits
  MAX_ENTRIES_PER_STORE: 100,
} as const;

// Store Definitions

const stores = {
  // Editor history stores
  emailBuilderHistory: localforage.createInstance({
    name: STORAGE_CONFIG.DB_NAME,
    storeName: "email_builder_history",
    description: "Email builder undo/redo history",
  }),
  workflowHistory: localforage.createInstance({
    name: STORAGE_CONFIG.DB_NAME,
    storeName: "workflow_history",
    description: "Workflow builder undo/redo history",
  }),

  // CTWA Ads Management stores
  ctwaTargetSearch: localforage.createInstance({
    name: STORAGE_CONFIG.DB_NAME,
    storeName: "ctwa_target_search",
    description: "CTWA target search results cache",
  }),
  ctwaTargetingCategories: localforage.createInstance({
    name: STORAGE_CONFIG.DB_NAME,
    storeName: "ctwa_targeting_categories",
    description: "CTWA targeting categories cache (interests, behaviors, etc.)",
  }),

  // Property Management store
  propertyGroups: localforage.createInstance({
    name: STORAGE_CONFIG.DB_NAME,
    storeName: "property_groups",
    description: "Property groups cache for offline-first support",
  }),

  // Generic stores
  largeDataCache: localforage.createInstance({
    name: STORAGE_CONFIG.DB_NAME,
    storeName: "large_data_cache",
    description: "Cache for large API responses",
  }),
  tempFormData: localforage.createInstance({
    name: STORAGE_CONFIG.DB_NAME,
    storeName: "temp_form_data",
    description: "Temporary form data storage",
  }),
};

export type StoreName = keyof typeof stores;
export interface StorageItem<T> {
  data: T;
  timestamp: number;
  expiresAt?: number;
  version?: number;
  metadata?: Record<string, unknown>;
}
export interface CacheEntry<T> extends StorageItem<T> {
  key: string;
}
export interface CacheStats {
  totalEntries: number;
  totalSizeEstimate: number;
  expiredEntries: number;
  entriesByKey: Record<string, number>;
}

export function isIndexedDBAvailable(): boolean {
  try {
    return typeof indexedDB !== "undefined";
  } catch {
    return false;
  }
}

export function getStore(storeName: StoreName): LocalForage {
  return stores[storeName];
}

export function isExpired(expiresAt: number | undefined): boolean {
  if (!expiresAt) return false;
  return Date.now() > expiresAt;
}

export async function getStorageItem<T>(
  storeName: StoreName,
  key: string,
): Promise<T | null> {
  try {
    const item = await stores[storeName].getItem<StorageItem<T>>(key);
    if (!item) return null;

    // Check expiration
    if (isExpired(item.expiresAt)) {
      await stores[storeName].removeItem(key);
      return null;
    }

    return item.data;
  } catch (error) {
    console.error(`[IndexedDB] Error getting item ${key}:`, error);
    return null;
  }
}

export async function getStorageItemWithMeta<T>(
  storeName: StoreName,
  key: string,
): Promise<StorageItem<T> | null> {
  try {
    const item = await stores[storeName].getItem<StorageItem<T>>(key);
    if (!item) return null;
    if (isExpired(item.expiresAt)) {
      await stores[storeName].removeItem(key);
      return null;
    }
    return item;
  } catch (error) {
    console.error(`[IndexedDB] Error getting item with meta ${key}:`, error);
    return null;
  }
}

/**
 * Set item in storage
 */
export async function setStorageItem<T>(
  storeName: StoreName,
  key: string,
  data: T,
  ttlMs?: number,
  metadata?: Record<string, unknown>,
): Promise<void> {
  try {
    const existing = await stores[storeName].getItem<StorageItem<T>>(key);
    const version = (existing?.version ?? 0) + 1;
    const item: StorageItem<T> = {
      data,
      timestamp: Date.now(),
      expiresAt: ttlMs ? Date.now() + ttlMs : undefined,
      version,
      metadata,
    };
    await stores[storeName].setItem(key, item);
  } catch (error) {
    console.error(`[IndexedDB] Error setting item ${key}:`, error);
  }
}

export async function removeStorageItem(
  storeName: StoreName,
  key: string,
): Promise<void> {
  try {
    await stores[storeName].removeItem(key);
  } catch (error) {
    console.error(`[IndexedDB] Error removing item ${key}:`, error);
  }
}

export async function hasValidItem(
  storeName: StoreName,
  key: string,
): Promise<boolean> {
  try {
    const item = await stores[storeName].getItem<StorageItem<unknown>>(key);
    if (!item) return false;
    return !isExpired(item.expiresAt);
  } catch {
    return false;
  }
}

// ============================================
// Bulk Operations
// ============================================

/**
 * Get all items in a store (excluding expired)
 */
export async function getAllItems<T>(
  storeName: StoreName,
): Promise<Record<string, T>> {
  try {
    const result: Record<string, T> = {};
    const now = Date.now();
    const expiredKeys: string[] = [];

    await stores[storeName].iterate<StorageItem<T>, void>((item, key) => {
      if (item.expiresAt && item.expiresAt <= now) {
        expiredKeys.push(key);
      } else {
        result[key] = item.data;
      }
    });
    if (expiredKeys.length > 0) {
      Promise.all(
        expiredKeys.map((k) => stores[storeName].removeItem(k)),
      ).catch(console.error);
    }
    return result;
  } catch (error) {
    console.error(`[IndexedDB] Error getting all items:`, error);
    return {};
  }
}

export async function getMultipleItems<T>(
  storeName: StoreName,
  keys: string[],
): Promise<Record<string, T>> {
  try {
    const result: Record<string, T> = {};

    await Promise.all(
      keys.map(async (key) => {
        const data = await getStorageItem<T>(storeName, key);
        if (data !== null) {
          result[key] = data;
        }
      }),
    );
    return result;
  } catch (error) {
    console.error(`[IndexedDB] Error getting multiple items:`, error);
    return {};
  }
}

export async function setMultipleItems<T>(
  storeName: StoreName,
  items: Record<string, T>,
  ttlMs?: number,
): Promise<void> {
  try {
    await Promise.all(
      Object.entries(items).map(([key, data]) =>
        setStorageItem(storeName, key, data, ttlMs),
      ),
    );
  } catch (error) {
    console.error(`[IndexedDB] Error setting multiple items:`, error);
  }
}

export async function removeMultipleItems(
  storeName: StoreName,
  keys: string[],
): Promise<void> {
  try {
    await Promise.all(keys.map((key) => stores[storeName].removeItem(key)));
  } catch (error) {
    console.error(`[IndexedDB] Error removing multiple items:`, error);
  }
}

// ============================================
// Store Management
// ============================================

export async function clearStore(storeName: StoreName): Promise<void> {
  try {
    await stores[storeName].clear();
  } catch (error) {
    console.error(`[IndexedDB] Error clearing store ${storeName}:`, error);
  }
}

export async function clearAllStores(): Promise<void> {
  const results = await Promise.allSettled(
    Object.entries(stores).map(([name, store]) =>
      store.clear().catch((e) => {
        console.warn(`[IndexedDB] Failed to clear store "${name}":`, e);
      })
    )
  );
  const failed = results.filter((r) => r.status === "rejected").length;
  if (failed > 0) {
    console.warn(`[IndexedDB] ${failed} store(s) failed to clear`);
  } else {
    console.log("[IndexedDB] All stores cleared");
  }
}

export async function getStoreKeys(storeName: StoreName): Promise<string[]> {
  try {
    return await stores[storeName].keys();
  } catch (error) {
    console.error(`[IndexedDB] Error getting keys for ${storeName}:`, error);
    return [];
  }
}

export async function getStoreLength(storeName: StoreName): Promise<number> {
  try {
    return await stores[storeName].length();
  } catch (error) {
    console.error(`[IndexedDB] Error getting length for ${storeName}:`, error);
    return 0;
  }
}

// ============================================
// Cleanup Operations
// ============================================

export async function cleanupExpiredEntries(
  storeName: StoreName,
): Promise<number> {
  try {
    const now = Date.now();
    const keysToDelete: string[] = [];
    await stores[storeName].iterate<StorageItem<unknown>, void>((item, key) => {
      if (item.expiresAt && item.expiresAt <= now) {
        keysToDelete.push(key);
      }
    });
    await Promise.all(
      keysToDelete.map((key) => stores[storeName].removeItem(key)),
    );
    if (keysToDelete.length > 0) {
      console.log(
        `[IndexedDB] Cleaned up ${keysToDelete.length} expired entries from ${storeName}`,
      );
    }
    return keysToDelete.length;
  } catch (error) {
    console.error(`[IndexedDB] Error cleaning up ${storeName}:`, error);
    return 0;
  }
}

export async function cleanupAllExpiredEntries(): Promise<number> {
  let total = 0;
  for (const storeName of Object.keys(stores) as StoreName[]) {
    total += await cleanupExpiredEntries(storeName);
  }
  return total;
}

export async function enforceMaxEntries(
  storeName: StoreName,
  maxEntries: number = STORAGE_CONFIG.MAX_ENTRIES_PER_STORE,
): Promise<number> {
  try {
    const entries: Array<{ key: string; timestamp: number }> = [];
    await stores[storeName].iterate<StorageItem<unknown>, void>((item, key) => {
      entries.push({ key, timestamp: item.timestamp });
    });

    if (entries.length <= maxEntries) {
      return 0;
    }
    entries.sort((a, b) => a.timestamp - b.timestamp);
    const entriesToDelete = entries.slice(0, entries.length - maxEntries);
    await Promise.all(
      entriesToDelete.map(({ key }) => stores[storeName].removeItem(key)),
    );

    console.log(
      `[IndexedDB] Removed ${entriesToDelete.length} old entries from ${storeName}`,
    );
    return entriesToDelete.length;
  } catch (error) {
    console.error(
      `[IndexedDB] Error enforcing max entries in ${storeName}:`,
      error,
    );
    return 0;
  }
}

// ============================================
// Statistics
// ============================================

export async function getStoreStats(storeName: StoreName): Promise<CacheStats> {
  try {
    const now = Date.now();
    let totalEntries = 0;
    let totalSize = 0;
    let expiredEntries = 0;
    const entriesByKey: Record<string, number> = {};
    await stores[storeName].iterate<StorageItem<unknown>, void>((item, key) => {
      totalEntries++;
      totalSize += JSON.stringify(item).length;
      if (item.expiresAt && item.expiresAt <= now) {
        expiredEntries++;
      }
      const prefix = key.split(/[:|_]/)[0];
      entriesByKey[prefix] = (entriesByKey[prefix] || 0) + 1;
    });
    return {
      totalEntries,
      totalSizeEstimate: totalSize,
      expiredEntries,
      entriesByKey,
    };
  } catch (error) {
    console.error(`[IndexedDB] Error getting stats for ${storeName}:`, error);
    return {
      totalEntries: 0,
      totalSizeEstimate: 0,
      expiredEntries: 0,
      entriesByKey: {},
    };
  }
}

export async function getAllStoreStats(): Promise<
  Record<StoreName, CacheStats>
> {
  const result = {} as Record<StoreName, CacheStats>;
  for (const storeName of Object.keys(stores) as StoreName[]) {
    result[storeName] = await getStoreStats(storeName);
  }
  return result;
}

const EMAIL_BUILDER_HISTORY_KEY = "email_builder_history";
const MAX_HISTORY_SIZE = 30;
export interface EmailBuilderHistoryState<T> {
  past: T[];
  future: T[];
}

export async function saveEmailBuilderHistory<T>(
  history: EmailBuilderHistoryState<T>,
): Promise<void> {
  const trimmedHistory: EmailBuilderHistoryState<T> = {
    past: history.past.slice(-MAX_HISTORY_SIZE),
    future: history.future.slice(0, MAX_HISTORY_SIZE),
  };
  await setStorageItem(
    "emailBuilderHistory",
    EMAIL_BUILDER_HISTORY_KEY,
    trimmedHistory,
  );
}

export async function loadEmailBuilderHistory<
  T,
>(): Promise<EmailBuilderHistoryState<T> | null> {
  return getStorageItem<EmailBuilderHistoryState<T>>(
    "emailBuilderHistory",
    EMAIL_BUILDER_HISTORY_KEY,
  );
}

export async function clearEmailBuilderHistory(): Promise<void> {
  await removeStorageItem("emailBuilderHistory", EMAIL_BUILDER_HISTORY_KEY);
}

const WORKFLOW_HISTORY_KEY = "workflow_history";
export interface WorkflowHistoryState<T> {
  past: T[];
  future: T[];
}

export async function saveWorkflowHistory<T>(
  flowId: string,
  history: WorkflowHistoryState<T>,
): Promise<void> {
  const key = `${WORKFLOW_HISTORY_KEY}_${flowId}`;
  const trimmedHistory: WorkflowHistoryState<T> = {
    past: history.past.slice(-MAX_HISTORY_SIZE),
    future: history.future.slice(0, MAX_HISTORY_SIZE),
  };
  await setStorageItem("workflowHistory", key, trimmedHistory);
}

export async function loadWorkflowHistory<T>(
  flowId: string,
): Promise<WorkflowHistoryState<T> | null> {
  const key = `${WORKFLOW_HISTORY_KEY}_${flowId}`;
  return getStorageItem<WorkflowHistoryState<T>>("workflowHistory", key);
}

export async function clearWorkflowHistory(flowId: string): Promise<void> {
  const key = `${WORKFLOW_HISTORY_KEY}_${flowId}`;
  await removeStorageItem("workflowHistory", key);
}

export async function clearAllWorkflowHistories(): Promise<void> {
  await clearStore("workflowHistory");
}

export { stores };

export const __test__ = {
  isExpired,
  stores,
};
